import { useState, useEffect, Fragment } from 'react';
import { Popover, Tooltip } from '@mui/material';
import { DndContext, DragOverlay, useDroppable } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useSorting } from '../../contexts/SortingContext';
import "./Sorting.css"

export const SortingButton = ({ displayButton = true }) => {
  const {
    properties,
    order,
    asc,
    setOrder,
    setAsc,
    onOrderChange,
  } = useSorting();

  const [anchorEl, setAnchorEl] = useState(null);
  const [availableColumns, setAvailableColumns] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [preApplyAsc, setPreApplyAsc] = useState({});
  const [activeId, setActiveId] = useState(null);

  //   useEffect(() => {
  //   const allKeys = Object.keys(properties);
  //   setSelectedColumns(order);
  //   setAvailableColumns(allKeys.filter(k => !order.includes(k)));
  //   setPreApplyAsc(asc);
  // }, [order, asc, properties]);


  const handleClick = (e) => {
    setAnchorEl(e.currentTarget);

    const allKeys = Object.keys(properties);
    setSelectedColumns(order);
    setAvailableColumns(allKeys.filter(k => !order.includes(k)));
    setPreApplyAsc(asc);
  };

  const handleClose = () => setAnchorEl(null);
  const open = Boolean(anchorEl);

  const handleDragStart = (event) => setActiveId(event.active.id);

const handleDragOver = (event) => {
  const { active, over } = event;
  if (!over) return;
 
};

const handleDragEnd = (event) => {
  const { active, over } = event;

  if (!over) {
    setActiveId(null);
    return;
  }

  const activeId = active.id;
  const overId = over.id;

  const from = active.data.current?.source;
  const to = over.data.current?.source;

  if (activeId === overId) {
    setActiveId(null);
    return;
  }

  // re-order
  if (from === to) {
    if (from === 'available') {
      const oldIndex = availableColumns.indexOf(activeId);
      const newIndex = availableColumns.indexOf(overId);
      setAvailableColumns(arrayMove(availableColumns, oldIndex, newIndex));
    } else {
      const oldIndex = selectedColumns.indexOf(activeId);
      const newIndex = selectedColumns.indexOf(overId);
      setSelectedColumns(arrayMove(selectedColumns, oldIndex, newIndex));
    }
  } 
  // Movement between lists
  else {
    if (from === 'available') {
      // Mpve to selected list
      setAvailableColumns(prev => prev.filter(item => item !== activeId));
      setSelectedColumns(prev => [...prev, activeId]);
      setPreApplyAsc(prev => ({ ...prev, [activeId]: true }));
    } else {
      // Mover to available list
      setSelectedColumns(prev => prev.filter(item => item !== activeId));
      setAvailableColumns(prev => [...prev, activeId]);
      setPreApplyAsc(prev => {
        const copy = { ...prev };
        delete copy[activeId];
        return copy;
      });
    }
  }

  setActiveId(null);
};


  const updatePreAsc = (key, value) => {
    setPreApplyAsc(prev => ({ ...prev, [key]: value }));
  };

  const removeColumn = (key) => {
    setSelectedColumns(prev => prev.filter(i => i !== key));
    setAvailableColumns(prev => [...prev, key]);
    setPreApplyAsc(prev => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
  };

  const applySelection = () => {
    setOrder(selectedColumns);
    setAsc(preApplyAsc);
    onOrderChange(selectedColumns.map(k => `${k} ${preApplyAsc[k] ? 'asc' : 'desc'}`).join(','));
    handleClose();
  };

  return (
    <>
      {displayButton && <button className="sorting-btn-component" onClick={handleClick}><i className='icon-Sorting_button'></i></button>}
      <Popover
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <div className="sorting-menu">
          <div className="header">
            <div className="left">
              <b>Sorting Columns</b>
              <div className="sub-title">
                <i className="icon-info"></i>
                <span>Drag columns for hierarchical sorting</span>
              </div>
            </div>
            <div className="right">
              <button onClick={applySelection} className="filled small-size">Apply</button>
              <button onClick={handleClose} className="outlined icon small-size">
                <i className="icon-cross"></i>
              </button>
            </div>
          </div>

          <DndContext
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}>
            <div className="dnd-activity">
              <span>Available Columns</span>
              <span>Selected Columns</span>
              <SortableList id="available" items={availableColumns} labels={properties} source="available" />
              <SortableList
                id="selected"
                items={selectedColumns}
                labels={properties}
                source="selected"
                onArrowUp={(k) => updatePreAsc(k, true)}
                onArrowDown={(k) => updatePreAsc(k, false)}
                isAsc={(k) => preApplyAsc[k] === true}
                onRemoveItem={removeColumn}
              />
            </div>
            <DragOverlay>{activeId && <DragPreview id={activeId} label={properties[activeId]} />}</DragOverlay>
          </DndContext>
        </div>
      </Popover>
    </>
  );
};

const SortableList = ({ id, items, labels, source, onArrowUp, onArrowDown, isAsc, onRemoveItem }) => {
  const { setNodeRef } = useDroppable({ id });

  return (
    <div className="dnd-items" ref={setNodeRef}>
      <SortableContext strategy={verticalListSortingStrategy} items={items}>
        <div className="items-container">
          {items.map(key => (
            <SortableItem key={key} id={key} source={source}>
              {({ listeners, attributes }) => (
                <div className="sorting-component-dnd-item">
                  <div className="item-content">
                    <i className="icon-draggable" {...listeners} {...attributes}></i>
                    <span>{labels[key]}</span>
                  </div>
                  {onArrowUp && onArrowDown && (
                    <div className="item-actions">
                      <Tooltip title="Ascending">
                        <i
                          onClick={(e) => {
                            e.stopPropagation();
                            onArrowUp(key);
                          }}
                          className={`arrow-button icon-arrowup ${isAsc(key) ? 'active' : ''}`}
                        ></i>
                      </Tooltip>
                      <Tooltip title="Descending">
                        <i
                          onClick={(e) => {
                            e.stopPropagation();
                            onArrowDown(key);
                          }}
                          className={`arrow-button icon-arrowdown ${!isAsc(key) ? 'active' : ''}`}
                        ></i>
                      </Tooltip>
                      <Tooltip title="Remove">
                        <i
                          onClick={(e) => {
                            e.stopPropagation();
                            onRemoveItem(key);
                          }}
                          className="icon-light-cross"
                        ></i>
                      </Tooltip>
                    </div>
                  )}
                </div>
              )}
            </SortableItem>
          ))}
        </div>
      </SortableContext>
    </div>
  );
};

const DragPreview = ({ id, label }) => (
  <div className="sorting-component-dnd-item">
    <div className="item-content">
      <i className="icon-draggable"></i>
      <span>{label || id}</span>
    </div>
  </div>
);
const SortableItem = ({ id, children, source }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, data: { source } });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      {children({ listeners, attributes })}
    </div>
  );
};